/* eslint-disable import/prefer-default-export */
import { useCallback, useEffect, useState } from 'react';

import type { User } from '../../../../services';
import type { EntitlementRow } from '../IncludedEntitlements';

/**
 * Custom hook to manage sidepanel state for IncludedEntitlements component
 * Handles opening/closing sidepanel based on row selections and manages data/type state
 */
export const useSidePanelState = (
  selectedRowData: User | EntitlementRow | null,
  setSelectedRowData: (data: User | EntitlementRow | null) => void
) => {
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [sidePanelData, setSidePanelData] = useState<
    User | EntitlementRow | null
  >(null);
  const [sidePanelType, setSidePanelType] = useState<'user' | 'entitlement'>(
    'user'
  );

  // Watch selectedRowData changes and open sidepanel with appropriate data
  useEffect(() => {
    if (selectedRowData) {
      // Type guard for User data
      if ('userkey' in selectedRowData && 'firstname' in selectedRowData) {
        setSidePanelData(selectedRowData);
        setSidePanelType('user');
        setIsSidePanelOpen(true);
      }
      // Type guard for EntitlementRow data
      else if ('evKey' in selectedRowData) {
        setSidePanelData(selectedRowData);
        setSidePanelType('entitlement');
        setIsSidePanelOpen(true);
      }
    }
  }, [selectedRowData]);

  // Handle closing sidepanel and clearing all related state
  const handleCloseSidePanel = useCallback(() => {
    setIsSidePanelOpen(false);
    setSidePanelData(null);
    setSelectedRowData(null); // Clear the row selection
  }, [setSelectedRowData]);

  return {
    isSidePanelOpen,
    sidePanelData,
    sidePanelType,
    handleCloseSidePanel,
  };
};
